import { memo, useCallback, useMemo, useState } from "react";
import { STAGE_INFO, fmtDate, fmtMoney, rel, type BoardRow } from "@/lib/event-board";
import { sortRows, summarize, type RowSort, type SortKey } from "@/lib/event-filters";
import { cn } from "@/lib/utils";
import { EventFlags, QuoteStatusDot } from "../EventFlags";

interface EventsListViewProps {
  rows: BoardRow[];
  today: Date;
  /** The Event cell opens the event's detail sheet. */
  onOpenDetail: (eventId: string) => void;
}

const TH =
  "whitespace-nowrap border-b px-2.5 py-2.5 text-left sm:px-3.5 text-[11.5px] font-bold uppercase tracking-[.07em] text-muted-foreground";
const TD = "border-b px-2.5 py-2.5 align-top sm:px-3.5 group-last:border-b-0 group-hover:bg-background";
const SUB = "block text-[12.5px] text-muted-foreground";

/** Post-event rows also show what's wrapped up. */
function wrapUpPills(row: BoardRow): string[] {
  if (row.stage !== "post_event") return [];
  return [row.owing > 0 ? "" : "Paid", row.thanked ? "Thanked" : "", row.reviewAsked ? "Review asked" : ""].filter(
    Boolean,
  );
}

const ListRow = memo(function ListRow({
  row,
  today,
  onOpenDetail,
}: { row: BoardRow } & Omit<EventsListViewProps, "rows">) {
  const stage = STAGE_INFO[row.stage];
  return (
    <tr className="group">
      <td className={TD}>
        {/* A max-width on the cell itself is ignored in auto table layout, so it goes on this block.
            `anywhere`, not `break-words`: a name with no break in it (an email) would otherwise set the
            column's minimum width and push the List into a sideways scroll at 1440. */}
        <div className="w-[110px] [overflow-wrap:anywhere] sm:w-auto sm:max-w-[180px]">
          <button type="button" onClick={() => onOpenDetail(row.id)} className="p-0 text-left font-bold hover:underline">
            {row.name}
          </button>
          {row.occasion && <span className={SUB}>{row.occasion}</span>}
        </div>
      </td>
      <td className={TD}>
        <span className="whitespace-nowrap">{row.date ? fmtDate(row.date, today) : "Not set"}</span>
        <span className={SUB}>{row.daysOut !== null ? rel(row.daysOut) : ""}</span>
      </td>
      <td className={cn(TD, "text-right")}>{row.guests}</td>
      <td className={TD}>{row.serviceLabel}</td>
      <td className={TD}>
        <span
          className="inline-block whitespace-nowrap rounded border-l-[3px] bg-muted px-[9px] py-0.5 text-[11.5px] font-bold text-foreground"
          style={{ borderLeftColor: stage.color }}
        >
          {stage.label}
        </span>
      </td>
      <td className={TD}>
        <span className="inline-flex items-center gap-[5px] font-semibold">
          <QuoteStatusDot status={row.quoteStatus} />
          {row.quoteStatusLabel}
        </span>
        {row.quote && <span className={cn(SUB, "whitespace-nowrap")}>{row.quote.quote_number}</span>}
      </td>
      <td className={cn(TD, "text-right")}>{row.quote ? fmtMoney(row.total) : ""}</td>
      <td className={cn(TD, "text-right", row.owing > 0 && "font-bold text-[hsl(var(--ev-warn))]")}>
        {row.owing > 0 ? fmtMoney(row.owing) : ""}
      </td>
      <td className={TD}>
        <div className="max-w-[160px]">
          <EventFlags flags={row.flags} extra={wrapUpPills(row)} wrapText />
        </div>
      </td>
    </tr>
  );
});

function SortHeader({
  k,
  label,
  sort,
  onSort,
}: {
  k: SortKey;
  label: string;
  sort: RowSort;
  onSort: (k: SortKey) => void;
}) {
  const on = sort.key === k;
  return (
    <th
      className={cn(TH, k === "total" && "text-right")}
      aria-sort={on ? (sort.dir > 0 ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn("p-0 uppercase tracking-[inherit]", on && "text-foreground")}
      >
        {label}
        {on ? (sort.dir > 0 ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );
}

/** /admin/events List (#1557): the same filtered events as the Pipeline, as a table. */
export function EventsListView({ rows, today, onOpenDetail }: EventsListViewProps) {
  const [sort, setSort] = useState<RowSort>({ key: "date", dir: 1 });
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const totals = useMemo(() => summarize(rows), [rows]);
  const onSort = useCallback(
    (k: SortKey) => setSort((s) => (s.key === k ? { key: k, dir: s.dir > 0 ? -1 : 1 } : { key: k, dir: 1 })),
    [],
  );

  return (
    <div className="overflow-x-auto rounded-[10px] border bg-card shadow-[0_1px_2px_hsl(30_20%_20%/.06),0_4px_14px_hsl(30_20%_20%/.06)]">
      <table className="w-full border-collapse sm:min-w-[860px] lg:min-w-0 text-sm tabular-nums">
        <thead>
          <tr>
            <th className={TH}>Event</th>
            <SortHeader k="date" label="Date" sort={sort} onSort={onSort} />
            <th className={cn(TH, "text-right")}>Guests</th>
            <th className={TH}>Service</th>
            <th className={TH}>Stage</th>
            <th className={TH}>Quote</th>
            <SortHeader k="total" label="Total" sort={sort} onSort={onSort} />
            <th className={cn(TH, "text-right")}>Owing</th>
            <th className={TH}>Flags</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={9} className="px-3.5 py-3 text-[13px] text-muted-foreground">
                No events match these filters.
              </td>
            </tr>
          ) : (
            sorted.map((r) => <ListRow key={r.id} row={r} today={today} onOpenDetail={onOpenDetail} />)
          )}
        </tbody>
        {sorted.length > 0 && (
          <tfoot>
            <tr className="font-bold [&>td]:border-t-2 [&>td]:px-3.5 [&>td]:py-2.5">
              <td colSpan={6}>
                {totals.events} event{totals.events === 1 ? "" : "s"}
              </td>
              <td className="text-right">{fmtMoney(totals.quoted)}</td>
              <td className="text-right">{totals.owing > 0 ? fmtMoney(totals.owing) : ""}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
